import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGIN, REGISTER } from "@/constants/testIds";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="eyebrow mb-2 block">{label}</span>
    <Input {...props} className="h-12 border-slate-800 bg-[#131927] text-base focus-visible:ring-[#FF2D78]" />
  </label>
);

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (user) return <Navigate to="/players" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && form.password !== form.confirm) return setError("Passwords do not match");
    setBusy(true);
    try {
      await login(form.email, form.password);
      navigate("/players");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain relative grid min-h-screen bg-[#0B0F19] lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:block">
        <img src="https://images.unsplash.com/photo-1629977007371-0ba395424741?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200" alt="Athlete"
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19]/30 to-[#0B0F19]" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <p className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tight">Every stat.<br />Every athlete.</p>
        </div>
      </div>
      <div className="relative z-10 flex flex-col px-6 py-8 sm:px-12">
        <Logo />
        <form onSubmit={submit} className="rise mx-auto my-auto w-full max-w-md space-y-5 py-12" data-testid={`${mode}-form`}>
          <div className="mb-8">
            <p className="eyebrow !text-[#FF2D78]">{isLogin ? "Welcome back" : "Free membership"}</p>
            <h1 className="mt-3 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">
              {isLogin ? "Member log in" : "Create your account"}
            </h1>
          </div>
          {!isLogin && <Field label="Full name" value={form.name} onChange={set("name")} required data-testid={REGISTER.nameInput} />}
          <Field label="Email" type="email" value={form.email} onChange={set("email")} required autoComplete="email"
            data-testid={isLogin ? LOGIN.emailInput : REGISTER.emailInput} />
          <Field label="Password" type="password" value={form.password} onChange={set("password")} required minLength={isLogin ? 1 : 6}
            autoComplete={isLogin ? "current-password" : "new-password"} data-testid={isLogin ? LOGIN.passwordInput : REGISTER.passwordInput} />
          {!isLogin && <Field label="Confirm password" type="password" value={form.confirm} onChange={set("confirm")} required
            data-testid={REGISTER.passwordConfirmInput} />}
          {error && <p data-testid="auth-error-message" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
          <button type="submit" disabled={busy} data-testid={isLogin ? LOGIN.submitButton : REGISTER.submitButton}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#FF2D78] font-semibold text-white transition-colors duration-200 hover:bg-[#E01F66] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isLogin ? "Log in" : "Join Know Her Name"}
          </button>
          <p className="text-center text-sm text-slate-400">
            {isLogin ? "Not a member yet? " : "Already a member? "}
            <Link to={isLogin ? "/request-access" : "/login"} data-testid={isLogin ? LOGIN.registerLink : REGISTER.loginLink}
              className="font-semibold text-white underline-offset-4 hover:underline">
              {isLogin ? "Request an invitation" : "Log in"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
